import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { styled } from "@mui/material";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    Chip,
    Box,
    Checkbox,
    FormGroup,
    Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import flaskServer from '../config/flaskserver';
import { MODULOS_SISTEMA } from './CadastroUsuario';

const useStyles = styled((theme) => ({
    tableContainer: { marginTop: theme.spacing(2) },
    headerCell: { fontWeight: 'bold' },
    deleteButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': { backgroundColor: theme.palette.error.dark },
    },
    actionButton: { marginLeft: theme.spacing(1) },
    actionCell: { display: 'flex', justifyContent: 'space-around' },
}));

const ListaUsuarios = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const classes = useStyles();

    const [editUser, setEditUser] = useState(null);
    const [editUserData, setEditUserData] = useState({});
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/users`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            setUsers(response.data.users);
            setLoading(false);
        } catch (error) { }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleDelete = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${flaskServer.flaskServerIP}/users/${userId}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            setUsers(users.filter(user => user.id !== userId));
        } catch (error) { }
    };

    const handleOpenEditDialog = (user) => {
        setEditUser(user);
        setEditUserData({
            username: user.username,
            email: user.email,
            nomeEmpresa: user.nome_empresa,
            celular: user.celular,
            numAccessesAllowed: user.num_accesses_allowed,
            validade: user.validade,
            status: user.status,
            modulos: user.modulos || [],
        });
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => { setOpenEditDialog(false); setEditUser(null); };

    const handleSaveEdit = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${flaskServer.flaskServerIP}/update-profile`, {
                ...editUserData,
                userId: editUser.id,
            }, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (response.status === 200) {
                setUsers(users.map(user => user.id === editUser.id
                    ? { ...user, ...editUserData, nome_empresa: editUserData.nomeEmpresa, num_accesses_allowed: editUserData.numAccessesAllowed }
                    : user));
                handleCloseEditDialog();
            }
        } catch (error) { }
    };

    const handleOpenPasswordDialog = (user) => {
        setEditUser(user);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setOpenPasswordDialog(true);
    };
    const handleClosePasswordDialog = () => { setOpenPasswordDialog(false); setEditUser(null); };

    const handleSavePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${flaskServer.flaskServerIP}/update-password`, {
                newPassword: passwordData.newPassword,
                userId: editUser.id,
            }, { headers: { 'Authorization': `Bearer ${token}` } });
            if (response.status === 200) handleClosePasswordDialog();
        } catch (error) { }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditUserChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditUserData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleModuloEditChange = (key) => {
        setEditUserData(prev => ({
            ...prev,
            modulos: prev.modulos.includes(key)
                ? prev.modulos.filter(m => m !== key)
                : [...prev.modulos, key],
        }));
    };

    const getModuloLabel = (key) => {
        const m = MODULOS_SISTEMA.find(m => m.key === key);
        return m ? m.label.split('(')[0].trim() : key;
    };

    return (
        <div>
            <Typography variant="h4" gutterBottom>Lista de Usuários</Typography>
            {loading ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Admin</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Acessos</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Validade</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Módulos</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.id}</TableCell>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{user.is_admin ? 'Sim' : 'Não'}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.nome_empresa}</TableCell>
                                    <TableCell>{user.num_accesses_allowed}</TableCell>
                                    <TableCell>{user.validade}</TableCell>
                                    <TableCell>{user.status ? 'Ativo' : 'Inativo'}</TableCell>
                                    <TableCell>
                                        {user.is_admin ? (
                                            <Chip label="Admin (tudo)" color="primary" size="small" />
                                        ) : (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {(user.modulos || []).length === 0
                                                    ? <Typography variant="caption" color="text.secondary">Nenhum</Typography>
                                                    : (user.modulos || []).map(m => (
                                                        <Chip key={m} label={getModuloLabel(m)} size="small" variant="outlined" />
                                                    ))
                                                }
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Button variant="contained" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDelete(user.id)}>
                                                Excluir
                                            </Button>
                                            <Button variant="contained" color="primary" size="small" onClick={() => handleOpenEditDialog(user)}>
                                                Editar
                                            </Button>
                                            <Button variant="contained" color="secondary" size="small" onClick={() => handleOpenPasswordDialog(user)}>
                                                Senha
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog – Editar usuário */}
            <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Editar Usuário</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}><TextField label="Username" name="username" value={editUserData.username || ''} onChange={handleEditUserChange} fullWidth /></Grid>
                        <Grid item xs={12}><TextField label="Email" name="email" value={editUserData.email || ''} onChange={handleEditUserChange} fullWidth /></Grid>
                        <Grid item xs={12}><TextField label="Nome da Empresa" name="nomeEmpresa" value={editUserData.nomeEmpresa || ''} onChange={handleEditUserChange} fullWidth /></Grid>
                        <Grid item xs={12}><TextField label="Celular" name="celular" value={editUserData.celular || ''} onChange={handleEditUserChange} fullWidth /></Grid>
                        <Grid item xs={12}><TextField label="Acessos Permitidos" type="number" name="numAccessesAllowed" value={editUserData.numAccessesAllowed || 1} onChange={handleEditUserChange} fullWidth /></Grid>
                        <Grid item xs={12}>
                            <TextField label="Validade" type="date" name="validade" value={editUserData.validade || ''} onChange={handleEditUserChange} fullWidth InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={!!editUserData.status} onChange={handleEditUserChange} name="status" color="primary" />}
                                label="Status"
                            />
                        </Grid>

                        {/* Módulos */}
                        <Grid item xs={12}>
                            <Divider sx={{ mb: 1 }} />
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Módulos liberados</Typography>
                            <FormGroup>
                                {MODULOS_SISTEMA.map((modulo) => (
                                    <FormControlLabel
                                        key={modulo.key}
                                        control={
                                            <Checkbox
                                                checked={(editUserData.modulos || []).includes(modulo.key)}
                                                onChange={() => handleModuloEditChange(modulo.key)}
                                                color="primary"
                                            />
                                        }
                                        label={modulo.label}
                                    />
                                ))}
                            </FormGroup>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog} color="secondary">Cancelar</Button>
                    <Button onClick={handleSaveEdit} color="primary" variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog – Alterar senha */}
            <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog}>
                <DialogTitle>Alterar Senha</DialogTitle>
                <DialogContent>
                    <TextField label="Nova Senha" type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} fullWidth margin="normal" />
                    <TextField label="Confirme a Senha" type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} fullWidth margin="normal" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePasswordDialog} color="secondary">Cancelar</Button>
                    <Button onClick={handleSavePassword} color="primary" variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ListaUsuarios;